import React from 'react';
import {
  Box, Container, Typography, Button, Grid, Stack,
} from '@mui/material';
import { LocalShipping, DirectionsCar, ArrowForward } from '@mui/icons-material';

const COURIER_URL = process.env.REACT_APP_COURIER_URL || 'http://localhost:3000';
const DRIVER_URL = process.env.REACT_APP_DRIVER_URL || 'http://localhost:3001';

export default function CTA() {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(160deg, #071525 0%, #0d1b2a 50%, #0a1e35 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background orbs */}
      <Box sx={{
        position: 'absolute', top: '-20%', left: '-10%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(22,163,74,0.1) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-20%', right: '-10%',
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(234,88,12,0.1) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Typography
            variant="h2"
            sx={{
              color: '#fff',
              fontSize: { xs: '2rem', md: '3rem', lg: '3.5rem' },
              mb: 2.5,
            }}
          >
            Pronto para simplificar{' '}
            <Box component="span" sx={{ color: '#4ade80' }}>as suas entregas</Box>?
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: { xs: '1rem', md: '1.15rem' },
              maxWidth: 560,
              mx: 'auto',
              mb: 5,
              lineHeight: 1.75,
            }}
          >
            Junte-se a mais de 1 200 empresas que já utilizam a NeoDrive para gerir equipas,
            automatizar remunerações e pagar a todos a tempo — todas as semanas.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            <Button
              variant="contained"
              size="large"
              href={COURIER_URL + '/register'}
              startIcon={<LocalShipping />}
              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
              sx={{
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                px: 4,
                py: 1.8,
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #15803d, #166534)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px rgba(22,163,74,0.5)',
                },
              }}
            >
              Começar com a plataforma de estafetas
            </Button>
            <Button
              variant="contained"
              size="large"
              href={DRIVER_URL + '/register'}
              startIcon={<DirectionsCar />}
              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
              sx={{
                background: 'linear-gradient(135deg, #ea580c, #c2410c)',
                px: 4,
                py: 1.8,
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c2410c, #9a3412)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px rgba(234,88,12,0.5)',
                },
              }}
            >
              Começar com a plataforma de motoristas
            </Button>
          </Stack>

          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem' }}>
            Plano gratuito disponível · Sem cartão de crédito · Configuração em 2 minutos
          </Typography>
        </Box>

        {/* Two-column product summary */}
        <Grid container spacing={3}>
          {[
            {
              title: 'Plataforma de estafetas',
              description: 'Faça a gestão de equipas de estafetas, parceiros, relatórios semanais e pagamentos SEPA num só sítio.',
              color: '#4ade80',
              href: COURIER_URL + '/register',
              label: 'Começar →',
              features: ['Gestão de estafetas e parceiros', 'Automação da folha semanal', 'Pagamentos SEPA em lote', 'Faturas em PDF e envio'],
            },
            {
              title: 'Plataforma de motoristas',
              description: 'Faça a gestão da frota, rendas de veículos e pagamentos a proprietários com visibilidade total.',
              color: '#fb923c',
              href: DRIVER_URL + '/register',
              label: 'Começar →',
              features: ['Gestão de motoristas e veículos', 'Relatórios semanais de motoristas', 'Acompanhamento de rendas a proprietários', 'Pagamentos SEPA em lote'],
            },
          ].map((panel) => (
            <Grid item xs={12} md={6} key={panel.title}>
              <Box sx={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${panel.color}25`,
                borderRadius: 3,
                p: 3.5,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255,255,255,0.07)',
                  border: `1px solid ${panel.color}50`,
                },
              }}>
                <Typography variant="h5" sx={{ color: panel.color, fontWeight: 700, mb: 1 }}>
                  {panel.title}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', mb: 2.5, lineHeight: 1.6 }}>
                  {panel.description}
                </Typography>
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {panel.features.map((f) => (
                    <Grid item xs={6} key={f}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: panel.color, flexShrink: 0 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>{f}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Button
                  variant="outlined"
                  href={panel.href}
                  sx={{
                    borderColor: panel.color + '50',
                    color: panel.color,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: panel.color,
                      background: panel.color + '15',
                    },
                  }}
                >
                  {panel.label}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
